import lodashIsNumber from 'lodash-es/isNumber';
import type { ComputedRef, Ref } from 'vue';

import { type AuctionItemEntity, type AuctionListingItemEntity, AuctionState, BackendErrorCode } from '~/apiClient';
import type { InspectionExpiryStatus, SaleStatus } from '~/types/saleStatus';

interface UseAuctionStatusReturn extends SaleStatus {
    isCountdownVisible: ComputedRef<boolean>;
    activeAutoBidAmount: ComputedRef<number | undefined>;
}

type AuctionStatusTone = 'blue' | 'green' | 'yellow' | 'neutral';

export default function useAuctionStatus(auction: Ref<AuctionItemEntity | AuctionListingItemEntity>): UseAuctionStatusReturn {
    const { tt } = useTypedI18n();
    const { companyScopeIds } = useAuthUtils();
    const { extendNegotiation } = useExtendedNegotiation(companyScopeIds);
    const dayjs = useDayjs();

    const userMeta = computed(() => auction.value.userMeta ?? null);
    const bidInfo = computed(() => auction.value.bidInfo ?? null);
    const maxBidder = computed(
        () => !!userMeta.value?.maxBidder || !!userMeta.value?.colleagueMaxBidder || !!bidInfo.value?.maxBidder || !!bidInfo.value?.colleagueMaxBidder
    );
    const outBid = computed(() => !!userMeta.value?.outBid || (!!bidInfo.value && !maxBidder.value));

    const extendedNegotiation = computed(
        () =>
            auction.value.negotiation &&
            extendNegotiation(auction.value.negotiation, 'canActForSellerCompany' in auction.value ? auction.value.canActForSellerCompany : undefined)
    );

    // Indicates whether the auction or negotiation is awarded for buyer or seller users. Always false for third-party users or guests.
    const isAwardedForCurrentUser = computed(() => {
        if (auction.value.clearingPrice === null) return false;

        return (
            isSeller.value ||
            !!maxBidder.value ||
            !!extendedNegotiation.value?.isSuccessful ||
            ('orderId' in auction.value && !!auction.value.orderId)
        );
    });

    const isPriceHidden = computed(() => !lodashIsNumber(auction.value.currentPrice));

    const isFeeNoticeHidden = computed(
        () =>
            isPriceHidden.value ||
            !auction.value.state ||
            !(<AuctionState[]>[AuctionState.Waiting, AuctionState.Pending, AuctionState.Running, AuctionState.Expired]).includes(auction.value.state)
    );

    const isCountdownVisible = computed(() => auction.value.state === AuctionState.Pending || auction.value.state === AuctionState.Running);

    const tone = computed<AuctionStatusTone>(() => {
        if (isPriceHidden.value) return 'neutral';

        if (auction.value.state === AuctionState.Running) {
            if (maxBidder.value) return 'green';
            if (outBid.value) return 'yellow';

            return 'blue';
        }

        if (isAwardedForCurrentUser.value) return 'green';

        if (extendedNegotiation.value?.isInProgress) return 'blue';

        return 'neutral';
    });

    const statusSectionColor = computed(() => {
        const colors: Record<AuctionStatusTone, string> = {
            green: 'bg-success-50',
            blue: 'bg-primary-50',
            yellow: 'bg-amber-50',
            neutral: 'bg-neutral-100',
        } as const;

        return colors[tone.value];
    });

    const title = computed(() => {
        if (auction.value.soldOffPlatform) return tt('sale.soldOffPlatform');

        switch (auction.value.state) {
            case AuctionState.Draft:
            case AuctionState.ToBeApproved:
            case AuctionState.ToBeScheduled:
                return tt('sdp.Auction.auctionStatus.sellerReservePrice');
            case AuctionState.Pending:
                return tt('sdp.Auction.auctionStatus.startingPrice');
            case AuctionState.Running:
                return tt('sdp.Auction.auctionStatus.current');
            case AuctionState.Canceled:
                return tt(`sdp.Auction.state.${AuctionState.Canceled}`);
            case AuctionState.Expired:
                return isAwardedForCurrentUser.value ? tt('sdp.Auction.auctionStatus.wonAuction') : tt(`sdp.Auction.state.${AuctionState.Expired}`);
        }
    });

    const titleColor = computed(() => {
        return 'text-default';
    });

    const titleAnnotation = computed(() => {
        if (auction.value.state === AuctionState.Running) {
            if (userMeta.value?.maxBidder) {
                return tt(userMeta.value.colleagueMaxBidder ? 'sdp.Auction.auctionStatus.colleagueMaxBidder' : 'sdp.Auction.auctionStatus.maxBidder');
            }

            if (userMeta.value?.outBid) return tt('sdp.Auction.auctionStatus.outBid');
        }

        if (extendedNegotiation.value?.isInProgress) return tt('sdp.Auction.auctionStatus.negotiationInProgress');
    });

    const titleAnnotationColor = computed(() => {
        const colors: Record<AuctionStatusTone, string> = {
            green: 'text-success-600',
            yellow: 'text-warning-600',
            blue: 'text-primary-600',
            neutral: 'text-default',
        } as const;

        return colors[tone.value];
    });

    const priceColor = computed(() => {
        const colors: Record<AuctionStatusTone, string> = {
            green: 'text-primary-600',
            yellow: 'text-primary-600',
            blue: 'text-primary-600',
            neutral: 'text-default',
        } as const;

        return colors[tone.value];
    });

    const statusDotColor = computed(() => {
        const colors: Record<AuctionStatusTone, string> = {
            green: 'bg-success-500',
            yellow: 'bg-amber-500',
            blue: 'bg-primary',
            neutral: 'bg-neutral-500',
        } as const;

        return colors[tone.value];
    });

    const isBailNeeded = computed(() => !userMeta.value?.canBid && userMeta.value?.cantBidReason === BackendErrorCode.CantPerformUserNoBail);

    const purchaseBlockReasons = computed<BackendErrorCode[]>(() => {
        if (!userMeta.value || userMeta.value.canBid) return [];

        return getPurchaseBlockReasons({ purchaseBlockReasons: userMeta.value.purchaseBlockReasons, cantReason: userMeta.value.cantBidReason });
    });

    const hasPurchaseBlock = computed(() => purchaseBlockReasons.value.length > 0);

    const hasRestrictedNotice = computed(
        () =>
            !!userMeta.value &&
            !userMeta.value?.canBid &&
            userMeta.value?.cantBidReason !== BackendErrorCode.CantBidAuctionIsNotRunning &&
            !isBailNeeded.value &&
            !hasPurchaseBlock.value
    );

    const hasInspectionNotice = computed(() => !!userMeta.value?.inspectionExpiredExportRestrictions && !hasPurchaseBlock.value);

    const activeAutoBidAmount = computed<number | undefined>(() => (userMeta.value?.autoBid?.active ? userMeta.value?.autoBid?.amount : undefined));

    const isSeller = computed(() => !!auction.value && isSellerOfSale(auction.value, companyScopeIds.value));

    const inspectionReminder = computed<InspectionExpiryStatus | undefined>(() => {
        if (!isSeller.value) return;

        if (!auction.value.mainAsset.inspectionExpiryDate) return;

        const now = dayjs();
        const target = dayjs(auction.value.mainAsset.inspectionExpiryDate);

        if (target.isBefore(now)) return 'expired';
        if (target.isBefore(now.add(30, 'day'))) return 'expiringSoon';
    });

    return {
        statusSectionColor,
        title,
        titleColor,
        titleAnnotation,
        titleAnnotationColor,
        priceColor,
        statusDotColor,
        isFeeNoticeHidden,
        isCountdownVisible,
        hasRestrictedNotice,
        isBailNeeded,
        hasInspectionNotice,
        hasPurchaseBlock,
        purchaseBlockReasons,
        activeAutoBidAmount,
        isSeller,
        inspectionReminder,
    };
}
